import { DestroyRef, Injectable, inject, signal } from '@angular/core';

/**
 * Easter egg: Ctrl+Shift+P "flips" the whole page 180° — as if you turned the app
 * upside down. It's a live CSS transform on <body> (not a static screenshot), so the
 * UI stays fully interactive while flipped: the browser rotates hit-testing along with
 * the pixels, so clicks/typing still land on the right elements. Press the shortcut
 * again to flip back. transform never causes a layout shift, so the watchdog stays calm.
 */
@Injectable({ providedIn: 'root' })
export class PageFlipService {
  readonly flipped = signal(false);
  private initialized = false;

  constructor() {
    // Inject during construction (an injection context); init() may run later.
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('keydown', this.onKeydown);
    });
  }

  init(): void {
    // Idempotent: guard against double-registration if init() is ever called
    // more than once (e.g. test + app, or a future re-wire) — stacked listeners
    // would toggle the flip twice and cancel out.
    if (this.initialized || typeof document === 'undefined') return;
    this.initialized = true;
    document.addEventListener('keydown', this.onKeydown);
  }

  private onKeydown = (event: KeyboardEvent): void => {
    // event.code (physical key) is case/CapsLock/layout-independent — event.key
    // would be 'P' (uppercase) only while Shift is held and varies by layout.
    if (event.ctrlKey && event.shiftKey && event.code === 'KeyP') {
      event.preventDefault();
      this.toggle();
    }
  };

  private toggle(): void {
    const next = !this.flipped();
    this.flipped.set(next);

    const body = document.body;
    body.style.transition = 'transform 600ms cubic-bezier(0.4, 0, 0.2, 1)';
    body.style.transformOrigin = 'center center';
    body.style.transform = next ? 'rotate(180deg)' : '';
  }
}
